import { Injectable } from '@angular/core';
import { filterFrag } from './types/filter.types';
import { GridInput } from '@it2go/data-grid';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import {
    ServiceAnnouncementTypeGridObject,
    ServiceAnnouncementTypeObject,
} from '@libs/types/src/services/object-type/service/social-service/service-announcement-type.object';
import {
    ServiceAnnouncementTypeCreateInput,
} from '@libs/types/src/services/input-type/services/social-service/service-announcement-type.create-input';
import {
    ServiceAnnouncementTypePatchInput,
} from '@libs/types/src/services/input-type/services/social-service/service-announcement-type.patch-input';
import {
    ServiceAnnouncementFormGridObject,
    ServiceAnnouncementFormObject,
} from '@libs/types/src/services/object-type/service/social-service/service-announcement-form.object';
import {
    ServiceAnnouncementFormCreateInput,
} from '@libs/types/src/services/input-type/services/social-service/service-announcement-form.create-input';
import {
    ServiceAnnouncementFormPatchInput,
} from '@libs/types/src/services/input-type/services/social-service/service-announcement-form.patch-input';
import {
    WorkDataServiceAnnouncementValueGridObject,
    WorkDataServiceAnnouncementValueObject,
} from '@libs/types/src/services/object-type/service/social-service/work-data-service-announcement-value.object';
import {
    WorkDataServiceAnnouncementValueCreateInput,
} from '@libs/types/src/services/input-type/services/social-service/work-data-service-announcement-value.create-input';
import {
    WorkDataServiceAnnouncementValuePatchInput,
} from '@libs/types/src/services/input-type/services/social-service/work-data-service-announcement-value.patch-input';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { OkObject, WorkGridObject } from '@it2go/types';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';

@Injectable({ providedIn: 'root' })
export class WorkDataServiceAnnouncementGqlServiceV2 extends GqlServiceV2 {

    public getServiceAnnouncementTypes(input: GridInput, fragment: string) {
        return this.query<ServiceAnnouncementTypeGridObject>(
            `
                query($input: GridInput!) {
                    serviceAnnouncementType {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServiceAnnouncementType(input: ServiceAnnouncementTypeCreateInput, frag = 'id') {
        return this.mutate<ServiceAnnouncementTypeObject>(
            `
                mutation($input: ServiceAnnouncementTypeCreateInput!) {
                    serviceAnnouncementType {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public patchServiceAnnouncementType(input: ServiceAnnouncementTypePatchInput, frag = 'id') {
        return this.mutate<ServiceAnnouncementTypeObject>(
            `
                mutation($input: ServiceAnnouncementTypePatchInput!) {
                    serviceAnnouncementType {
                        patch(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceAnnouncementType(input: IdInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    serviceAnnouncementType {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceAnnouncementForms(input: GridInput, fragment: string) {
        return this.query<ServiceAnnouncementFormGridObject>(
            `
                query($input: GridInput!) {
                    serviceAnnouncementForm {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServiceAnnouncementForm(input: ServiceAnnouncementFormCreateInput, frag = 'id') {
        return this.mutate<ServiceAnnouncementFormObject>(
            `
                mutation($input: ServiceAnnouncementFormCreateInput!) {
                    serviceAnnouncementForm {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public patchServiceAnnouncementForm(input: ServiceAnnouncementFormPatchInput, frag = 'id') {
        return this.mutate<ServiceAnnouncementFormObject>(
            `
                mutation($input: ServiceAnnouncementFormPatchInput!) {
                    serviceAnnouncementForm {
                        patch(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceAnnouncementForm(input: IdInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    serviceAnnouncementForm {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getServiceAnnouncements(input: GridInput, fragment: string) {
        return this.query<WorkGridObject>(
            `
                query($input: GridInput!) {
                    work {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input: this.serviceAnnouncementWorkInput(input) },
        );
    }

    private serviceAnnouncementWorkInput(input: GridInput): GridInput {
        const columnMap: Record<string, string> = {
            assignedAt: 'workData.serviceAnnouncement.assignedAt',
            dateOfExpression: 'workData.serviceAnnouncement.dateOfExpression',
            sequenceNumber: 'workData.serviceAnnouncement.sequenceNumber',
            duration: 'workData.serviceAnnouncement.duration',
            state: 'workData.serviceAnnouncement.state',
            content: 'workData.serviceAnnouncement.content',
            solver: 'workData.serviceAnnouncement.solver',
            user: 'workData.serviceAnnouncement.user',
            folderId: 'workData.serviceAnnouncement.folderId',
            workId: 'id',
            type: 'workData.serviceAnnouncement.type',
            form: 'workData.serviceAnnouncement.form',
            createdAt: 'workData.serviceAnnouncement.createdAt',
        };
        const mapFilters = (filters: any[] | null | undefined): any[] | null | undefined => {
            return filters?.map((group) => Array.isArray(group)
                ? group.map((filter) => ({ ...filter, column: columnMap[filter.column] ?? filter.column }))
                : group);
        };

        return {
            ...input,
            filter: [
                ...(mapFilters(input?.filter) ?? []),
                [{ column: 'workType', operator: 'EQ', value: [WorkTypeEnum.SERVICE_ANNOUNCEMENT] }],
            ],
            additionalFilter: mapFilters((input as any)?.additionalFilter) as any,
            sorter: input?.sorter?.map((sorter: any) => ({
                ...sorter,
                column: columnMap[sorter.column] ?? sorter.column,
            })),
        };
    }

    public getServiceAnnouncementTagsForService(input: IdInput) {
        return this.query<string[]>(
            `
                query($input: IdInput!) {
                    workDataServiceAnnouncement {
                        serviceTags(input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public getServiceAnnouncementValues(input: GridInput, fragment: string) {
        return this.query<WorkDataServiceAnnouncementValueGridObject>(
            `
                query($input: GridInput!) {
                    workDataServiceAnnouncementValue {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createServiceAnnouncementValue(input: WorkDataServiceAnnouncementValueCreateInput, frag = 'id') {
        return this.mutate<WorkDataServiceAnnouncementValueObject>(
            `
                mutation($input: WorkDataServiceAnnouncementValueCreateInput!) {
                    workDataServiceAnnouncementValue {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public patchServiceAnnouncementValue(input: WorkDataServiceAnnouncementValuePatchInput, frag = 'id') {
        return this.mutate<WorkDataServiceAnnouncementValueObject>(
            `
                mutation($input: WorkDataServiceAnnouncementValuePatchInput!) {
                    workDataServiceAnnouncementValue {
                        patch(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteServiceAnnouncementValue(input: IdInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    workDataServiceAnnouncementValue {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

}
